import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Users,
  Merge,
  Search,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Mail,
  Phone,
  Building,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  ChevronRight,
  X,
  Zap,
  ListChecks
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactMerge = () => {
  const { getAuthHeaders, user } = useAuth();
  
  // Manual merge state
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [totalContacts, setTotalContacts] = useState(0);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [primaryContact, setPrimaryContact] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [merging, setMerging] = useState(false);
  
  // Bulk merge state
  const [activeTab, setActiveTab] = useState('manual');
  const [emailDuplicates, setEmailDuplicates] = useState([]);
  const [excludedEmails, setExcludedEmails] = useState(new Set());
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  
  const isAdmin = user?.roles?.includes('admin');

  const findDuplicates = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await axios.get(`${API}/contacts/duplicates/find?tenant=live`, {
        headers: getAuthHeaders()
      });
      setDuplicates(response.data.duplicates || []);
      setTotalContacts(response.data.total_contacts || 0);
      if (response.data.duplicates?.length === 0) {
        setMessage({ type: 'success', text: 'No duplicate contacts found!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to find duplicates' });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch email-only duplicates for bulk merge
  const findEmailDuplicates = useCallback(async () => {
    setBulkLoading(true);
    try {
      const response = await axios.get(`${API}/contacts/duplicates/email-only?tenant=live`, {
        headers: getAuthHeaders()
      });
      setEmailDuplicates(response.data.duplicate_groups || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to find email duplicates' });
    } finally {
      setBulkLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    findDuplicates();
  }, [findDuplicates]);

  useEffect(() => {
    if (activeTab === 'bulk') {
      findEmailDuplicates();
    }
  }, [activeTab, findEmailDuplicates]);

  // Toggle email exclusion
  const toggleEmailExclusion = (email) => {
    setExcludedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
    setBulkPreview(null); // Reset preview when exclusions change
  };

  // Preview bulk merge
  const previewBulkMerge = async () => {
    setBulkLoading(true);
    try {
      const response = await axios.post(
        `${API}/contacts/merge/bulk-preview?tenant=live`,
        { excluded_emails: Array.from(excludedEmails) },
        { headers: getAuthHeaders() }
      );
      setBulkPreview(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to preview bulk merge' });
    } finally {
      setBulkLoading(false);
    }
  };

  // Execute bulk merge
  const executeBulkMerge = async () => {
    if (!bulkPreview) return;
    
    setBulkExecuting(true);
    try {
      const response = await axios.post(
        `${API}/contacts/merge/bulk-execute?tenant=live`,
        { excluded_emails: Array.from(excludedEmails), dry_run: false },
        { headers: getAuthHeaders() }
      );
      
      setMessage({ 
        type: 'success', 
        text: response.data.message 
      });
      
      // Reset state and refresh
      setBulkPreview(null);
      setExcludedEmails(new Set());
      findEmailDuplicates();
      findDuplicates();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to execute bulk merge' });
    } finally {
      setBulkExecuting(false);
    }
  };

  // Manual merge functions
  // 08/12 (Josh): manual pairing — search ANY two contacts together even when
  // the duplicate scanner didn't flag them. Loads the full contact list once.
  const [manualQ, setManualQ] = useState('');
  const [allContacts, setAllContacts] = useState(null); // null = not loaded yet
  const ensureAllContacts = async () => {
    if (allContacts !== null) return;
    try {
      const res = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
      setAllContacts(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAllContacts([]);
    }
  };
  const manualMatches = (manualQ.trim().length >= 2 && Array.isArray(allContacts))
    ? allContacts.filter(c => {
        const q = manualQ.trim().toLowerCase();
        return (c.company_name || '').toLowerCase().includes(q)
          || (c.shortcode || '').toLowerCase().includes(q)
          || (c.email || '').toLowerCase().includes(q)
          || (c.name || '').toLowerCase().includes(q);
      }).filter(c => !selectedContacts.find(s => s.id === c.id)).slice(0, 10)
    : [];

  const toggleContactSelection = (contact) => {
    const exists = selectedContacts.find(c => c.id === contact.id);
    if (exists) {
      setSelectedContacts(prev => prev.filter(c => c.id !== contact.id));
      if (primaryContact?.id === contact.id) {
        setPrimaryContact(null);
      }
    } else {
      setSelectedContacts(prev => [...prev, contact]);
    }
  };

  const selectPair = (pair) => {
    setSelectedContacts([pair.contact1, pair.contact2]);
    setPrimaryContact(pair.contact1);
  };

  const setPrimary = (contact) => {
    if (!selectedContacts.find(c => c.id === contact.id)) {
      setSelectedContacts(prev => [...prev, contact]);
    }
    setPrimaryContact(contact);
  };

  const previewMultiMerge = async () => {
    if (!primaryContact || selectedContacts.length < 2) {
      setMessage({ type: 'error', text: 'Select at least 2 contacts and set a primary contact' });
      return;
    }

    const secondaryIds = selectedContacts
      .filter(c => c.id !== primaryContact.id)
      .map(c => c.id);

    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/contacts/merge/preview-multi`,
        { primary_id: primaryContact.id, secondary_ids: secondaryIds },
        { headers: getAuthHeaders() }
      );
      setPreviewData(response.data);
      setEditedContact(response.data.merged_result || {});
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to preview merge' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const openEditDialog = () => {
    if (previewData?.merged_result) {
      setEditedContact({ ...previewData.merged_result });
      setShowEditDialog(true);
    }
  };

  // 08/12 (Josh): shortcode is confirm/editable in the final step — check
  // availability so a collision with an UNRELATED contact is caught before
  // execute (a code held by one of the merged-away contacts is fine: they're
  // deleted before the survivor is stamped).
  const [scCheck, setScCheck] = useState(null); // null | {available, taken_by, ownedBySelected}
  const checkFinalShortcode = async () => {
    const code = (editedContact.shortcode || '').trim().toUpperCase();
    if (!code) { setScCheck(null); return; }
    try {
      const params = new URLSearchParams({ code });
      if (primaryContact?.id) params.append('exclude_id', primaryContact.id);
      const res = await axios.get(`${API}/contacts/check-shortcode?${params}`, { headers: getAuthHeaders() });
      const takenBy = res.data.taken_by;
      const ownedBySelected = !res.data.available && selectedContacts.some(
        (c) => c.id !== primaryContact?.id && (c.company_name || '') === takenBy);
      setScCheck({ ...res.data, ownedBySelected });
    } catch {
      setScCheck(null);
    }
  };

  const updateEditedField = (field, value) => {
    setEditedContact(prev => ({ ...prev, [field]: value }));
  };

  const executeMerge = async () => {
    if (!primaryContact || selectedContacts.length < 2) return;
    
    const secondaryIds = selectedContacts
      .filter(c => c.id !== primaryContact.id)
      .map(c => c.id);
    
    setMerging(true);
    try {
      const response = await axios.post(
        `${API}/contacts/merge/execute-multi`,
        { 
          primary_id: primaryContact.id, 
          secondary_ids: secondaryIds,
          final_contact_data: editedContact
        },
        { headers: getAuthHeaders() }
      );
      
      setMessage({ 
        type: 'success', 
        text: `${response.data.message}. References updated: ${response.data.total_updated}` 
      });
      
      setPreviewData(null);
      setSelectedContacts([]);
      setPrimaryContact(null);
      setEditedContact({});
      setShowEditDialog(false);
      findDuplicates();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to merge contacts' });
    } finally {
      setMerging(false);
    }
  };

  const getMatchBadgeColor = (type) => {
    switch (type) {
      case 'email': return 'bg-blue-100 text-blue-700';
      case 'phone': return 'bg-green-100 text-green-700';
      case 'company_name': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const clearSelection = () => {
    setSelectedContacts([]);
    setPrimaryContact(null);
    setPreviewData(null);
    setEditedContact({});
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-20">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold">Admin Access Required</h2>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-7xl mx-auto" data-testid="contact-merge-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Merge className="h-8 w-8 text-purple-600" />
              Contact Merge Tool
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Find and merge duplicate contacts into one master record
            </p>
          </div>
        </div>

        {/* Message */}
        {message && (
          <Alert className={`mb-6 ${
            message.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
          }`}>
            <AlertDescription className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Manual Merge
            </TabsTrigger>
            <TabsTrigger value="bulk" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Bulk Merge (Email Only)
            </TabsTrigger>
          </TabsList>

          {/* Manual Merge Tab */}
          <TabsContent value="manual">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{totalContacts}</p>
                      <p className="text-sm text-gray-500">Total Contacts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="text-2xl font-bold">{duplicates.length}</p>
                      <p className="text-sm text-gray-500">Duplicate Pairs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-purple-500" />
                    <div>
                      <p className="text-2xl font-bold">{selectedContacts.length}</p>
                      <p className="text-sm text-gray-500">Selected</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Merge className="h-8 w-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{primaryContact ? '✓' : '-'}</p>
                        <p className="text-sm text-gray-500">Primary Set</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {selectedContacts.length > 0 && (
                        <Button onClick={clearSelection} variant="outline" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      <Button onClick={findDuplicates} disabled={loading} variant="outline" size="sm">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-5 gap-6">
              {/* Duplicates List */}
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Potential Duplicates
                  </CardTitle>
                  <CardDescription>
                    Click a pair to select, or manually pick contacts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* 08/12 (Josh): manual pairing — put ANY two contacts together */}
                  <div className="mb-4" data-testid="manual-contact-search">
                    <Input
                      placeholder="Search any contact to add (name, shortcode, email)…"
                      value={manualQ}
                      onFocus={ensureAllContacts}
                      onChange={(e) => { setManualQ(e.target.value); ensureAllContacts(); }}
                      data-testid="manual-contact-search-input"
                    />
                    {manualQ.trim().length >= 2 && (
                      <div className="mt-1 border rounded-md divide-y max-h-56 overflow-y-auto bg-white shadow-sm">
                        {allContacts === null ? (
                          <div className="p-2 text-sm text-gray-500">Loading contacts…</div>
                        ) : manualMatches.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500">No matches (or already selected)</div>
                        ) : (
                          manualMatches.map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => { toggleContactSelection(c); setManualQ(''); }}
                              className="w-full text-left p-2 hover:bg-purple-50 flex items-center justify-between gap-2"
                              data-testid={`manual-add-${c.id}`}
                            >
                              <span className="text-sm truncate">
                                <span className="font-medium">{c.company_name}</span>
                                {c.shortcode && <span className="font-mono text-xs text-gray-500 ml-1">({c.shortcode})</span>}
                                {c.email && <span className="text-xs text-gray-400 ml-2">{c.email}</span>}
                              </span>
                              <span className="text-xs text-purple-700 font-semibold flex-shrink-0">+ Add</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">
                      Add two or more contacts, set one as primary, then Preview Merge — works even if they weren't flagged as duplicates.
                    </p>
                  </div>
                  {loading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                    </div>
                  ) : duplicates.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                      No duplicates found
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {duplicates.map((pair, idx) => (
                        <div
                          key={idx}
                          className="p-3 border rounded-lg transition-all hover:border-purple-300 hover:bg-purple-50/50"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={getMatchBadgeColor(pair.match_type)}>
                              {pair.match_type}
                            </Badge>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => selectPair(pair)}
                              data-testid={`select-pair-${idx}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Select
                            </Button>
                          </div>
                          
                          <div 
                            className={`p-2 rounded mb-2 cursor-pointer transition-all ${
                              selectedContacts.find(c => c.id === pair.contact1.id)
                                ? 'bg-purple-100 border-purple-300 border'
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => toggleContactSelection(pair.contact1)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={!!selectedContacts.find(c => c.id === pair.contact1.id)}
                                  onCheckedChange={() => toggleContactSelection(pair.contact1)}
                                />
                                <span className="font-medium text-sm truncate max-w-[150px]">
                                  {pair.contact1.company_name || 'N/A'}
                                </span>
                              </div>
                              {primaryContact?.id === pair.contact1.id && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Primary</Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 ml-6 mt-1">
                              {pair.contact1.email || pair.contact1.phone || 'No contact info'}
                            </div>
                          </div>
                          
                          <div 
                            className={`p-2 rounded cursor-pointer transition-all ${
                              selectedContacts.find(c => c.id === pair.contact2.id)
                                ? 'bg-purple-100 border-purple-300 border'
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => toggleContactSelection(pair.contact2)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={!!selectedContacts.find(c => c.id === pair.contact2.id)}
                                  onCheckedChange={() => toggleContactSelection(pair.contact2)}
                                />
                                <span className="font-medium text-sm truncate max-w-[150px]">
                                  {pair.contact2.company_name || 'N/A'}
                                </span>
                              </div>
                              {primaryContact?.id === pair.contact2.id && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Primary</Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 ml-6 mt-1">
                              {pair.contact2.email || pair.contact2.phone || 'No contact info'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Selected Contacts */}
              <Card className="col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="h-4 w-4" />
                    Selected ({selectedContacts.length})
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Click to set as primary
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedContacts.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm">
                      No contacts selected
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {selectedContacts.map(contact => (
                        <div
                          key={contact.id}
                          className={`p-2 rounded border cursor-pointer transition-all ${
                            primaryContact?.id === contact.id
                              ? 'bg-green-50 border-green-400'
                              : 'bg-gray-50 border-gray-200 hover:border-purple-300'
                          }`}
                          onClick={() => setPrimary(contact)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm truncate max-w-[120px]">
                              {contact.company_name || 'N/A'}
                            </span>
                            <div className="flex items-center gap-1">
                              {primaryContact?.id === contact.id ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">Keep</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-600 text-xs">Delete</Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleContactSelection(contact);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedContacts.length >= 2 && primaryContact && (
                    <Button 
                      className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                      onClick={previewMultiMerge}
                      disabled={previewLoading}
                    >
                      {previewLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ChevronRight className="h-4 w-4 mr-2" />
                      )}
                      Preview Merge
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Merge Preview */}
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Merge className="h-5 w-5" />
                    Merge Preview
                  </CardTitle>
                  <CardDescription>
                    Review and edit final contact before merging
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                    </div>
                  ) : !previewData ? (
                    <div className="text-center py-10 text-gray-500">
                      <Merge className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      Select contacts and click "Preview Merge"
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <Badge className="bg-green-100 text-green-700">Master Contact (Will Keep)</Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={openEditDialog}
                            data-testid="edit-final-contact-btn"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit Final Data
                          </Button>
                        </div>
                        <p className="font-semibold text-lg">{editedContact.company_name || previewData.primary.company_name}</p>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                          {(editedContact.email || previewData.merged_result?.email) && (
                            <div><Mail className="h-3 w-3 inline mr-1" />{editedContact.email || previewData.merged_result?.email}</div>
                          )}
                          {(editedContact.phone || previewData.merged_result?.phone) && (
                            <div><Phone className="h-3 w-3 inline mr-1" />{editedContact.phone || previewData.merged_result?.phone}</div>
                          )}
                        </div>
                      </div>

                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <Badge className="bg-red-100 text-red-700 mb-3">Will Be Deleted ({previewData.secondaries?.length || 0})</Badge>
                        <div className="space-y-2">
                          {previewData.secondaries?.map(sec => (
                            <div key={sec.id} className="flex items-center gap-2 text-sm">
                              <Trash2 className="h-3 w-3 text-red-500" />
                              <span className="font-medium">{sec.company_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {previewData.references_to_update?.total > 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="font-semibold text-amber-700 mb-2">
                            References to Update: {previewData.references_to_update.total}
                          </p>
                          <div className="grid grid-cols-3 gap-2 text-sm text-amber-600">
                            {previewData.references_to_update.syndications > 0 && (
                              <div>Syndications: {previewData.references_to_update.syndications}</div>
                            )}
                            {previewData.references_to_update.deals_as_broker > 0 && (
                              <div>Broker: {previewData.references_to_update.deals_as_broker}</div>
                            )}
                            {previewData.references_to_update.deals_as_funder > 0 && (
                              <div>Funder: {previewData.references_to_update.deals_as_funder}</div>
                            )}
                          </div>
                        </div>
                      )}

                      <Button 
                        onClick={executeMerge}
                        disabled={merging}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        data-testid="execute-merge-btn"
                      >
                        {merging ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Merging...</>
                        ) : (
                          <><Merge className="h-4 w-4 mr-2" /> Execute Merge</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bulk Merge Tab */}
          <TabsContent value="bulk">
            {/* Safety Notice */}
            <Alert className="mb-6 border-green-500 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700 text-sm">
                <strong>Safety Filters Active:</strong> Placeholder emails (like admin@powerfundingsolutions.com) 
                and contacts with syndicator/investor/funder/broker types are automatically excluded. 
                Only merchant-type duplicates are shown for bulk merge.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-6">
              {/* Email Duplicates List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-500" />
                    Email Duplicates (Merchants Only)
                  </CardTitle>
                  <CardDescription>
                    Contacts sharing the same email address (exact match, case-insensitive)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {bulkLoading && !emailDuplicates.length ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  ) : emailDuplicates.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                      <p>No email duplicates found</p>
                      <p className="text-xs mt-2 text-gray-400">
                        (Syndicator/investor/funder groups are excluded)
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            {emailDuplicates.length} email group{emailDuplicates.length !== 1 ? 's' : ''} found
                          </p>
                          <p className="text-xs text-blue-700">
                            {emailDuplicates.reduce((sum, g) => sum + g.count - 1, 0)} contacts can be merged
                          </p>
                        </div>
                        <Button 
                          onClick={findEmailDuplicates} 
                          variant="outline" 
                          size="sm"
                          disabled={bulkLoading}
                        >
                          <RefreshCw className={`h-4 w-4 ${bulkLoading ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {emailDuplicates.map((group, idx) => (
                          <div
                            key={idx}
                            className={`p-3 border rounded-lg transition-all ${
                              excludedEmails.has(group.email)
                                ? 'bg-gray-100 border-gray-300 opacity-60'
                                : 'bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Checkbox 
                                  checked={!excludedEmails.has(group.email)}
                                  onCheckedChange={() => toggleEmailExclusion(group.email)}
                                />
                                <Badge className="bg-blue-100 text-blue-700">
                                  {group.count} contacts
                                </Badge>
                              </div>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              <Mail className="h-3 w-3 inline mr-1" />
                              {group.email}
                            </p>
                            <div className="text-xs text-gray-500 space-y-1 ml-4">
                              {group.contacts.slice(0, 4).map((c, i) => (
                                <div key={c.id} className="flex items-center gap-1">
                                  {i === 0 ? (
                                    <Badge className="bg-green-100 text-green-600 text-[10px] px-1">Keep</Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-600 text-[10px] px-1">Delete</Badge>
                                  )}
                                  <span>{c.company_name}</span>
                                </div>
                              ))}
                              {group.contacts.length > 4 && (
                                <div className="text-gray-400">...and {group.contacts.length - 4} more</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Bulk Merge Preview & Execute */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Bulk Merge Preview
                  </CardTitle>
                  <CardDescription>
                    Review what will happen before executing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Preview Button */}
                  {!bulkPreview && (
                    <div className="space-y-4">
                      <Alert className="border-amber-400 bg-amber-50">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700 text-sm">
                          <strong>Important:</strong> This will merge contacts with matching emails only. 
                          The oldest contact (by creation date) will be kept as primary.
                          Uncheck groups in the list to exclude them.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="p-4 bg-gray-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600 mb-3">
                          {emailDuplicates.length - excludedEmails.size} of {emailDuplicates.length} email groups will be merged
                        </p>
                        <Button 
                          onClick={previewBulkMerge}
                          disabled={bulkLoading || emailDuplicates.length === 0 || emailDuplicates.length === excludedEmails.size}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="bulk-preview-btn"
                        >
                          {bulkLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <ListChecks className="h-4 w-4 mr-2" />
                          )}
                          Generate Preview
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Preview Results */}
                  {bulkPreview && (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-blue-700">{bulkPreview.total_merges}</p>
                          <p className="text-xs text-blue-600">Merge Operations</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-red-700">{bulkPreview.total_contacts_to_delete}</p>
                          <p className="text-xs text-red-600">Contacts to Delete</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg text-center col-span-2">
                          <p className="text-2xl font-bold text-amber-700">{bulkPreview.total_references_to_update}</p>
                          <p className="text-xs text-amber-600">References to Update</p>
                        </div>
                      </div>

                      {/* Merge List */}
                      <div className="border rounded-lg max-h-[250px] overflow-y-auto">
                        <div className="p-2 bg-gray-50 border-b sticky top-0">
                          <p className="text-xs font-medium text-gray-600">Merge Operations</p>
                        </div>
                        <div className="divide-y">
                          {bulkPreview.merge_operations.map((op, idx) => (
                            <div key={idx} className="p-2 text-xs">
                              <div className="flex items-center gap-1 mb-1">
                                <Mail className="h-3 w-3 text-blue-500" />
                                <span className="font-medium">{op.email}</span>
                              </div>
                              <div className="ml-4 text-gray-500">
                                Keep: <span className="text-green-600">{op.primary.company_name}</span>
                                {' • '}Delete: <span className="text-red-600">{op.contacts_to_delete}</span>
                                {' • '}Refs: <span className="text-amber-600">{op.references_to_update}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => setBulkPreview(null)}
                          className="flex-1"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button 
                          onClick={executeBulkMerge}
                          disabled={bulkExecuting}
                          className="flex-1 bg-red-600 hover:bg-red-700"
                          data-testid="bulk-execute-btn"
                        >
                          {bulkExecuting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4 mr-2" />
                          )}
                          Execute Bulk Merge
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Final Contact Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Final Contact Details
            </DialogTitle>
            <DialogDescription>
              Customize the final merged contact information.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={editedContact.company_name || ''}
                onChange={(e) => updateEditedField('company_name', e.target.value)}
                data-testid="edit-company-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dba">DBA (Doing Business As)</Label>
              <Input
                id="dba"
                value={editedContact.dba || ''}
                onChange={(e) => updateEditedField('dba', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="final_shortcode">Shortcode * (source of truth — stamps all deals)</Label>
              <Input
                id="final_shortcode"
                value={editedContact.shortcode || ''}
                onChange={(e) => { updateEditedField('shortcode', e.target.value.toUpperCase()); setScCheck(null); }}
                onBlur={checkFinalShortcode}
                className="font-mono"
                data-testid="edit-final-shortcode"
              />
              {scCheck && !scCheck.available && !scCheck.ownedBySelected && (
                <p className="text-xs text-red-600">
                  Taken by "{scCheck.taken_by}" — pick a different code or the merge will fail.
                </p>
              )}
              {scCheck && !scCheck.available && scCheck.ownedBySelected && (
                <p className="text-xs text-emerald-600">
                  Currently held by a contact being merged away — it frees up during the merge. ✓
                </p>
              )}
              {scCheck && scCheck.available && (
                <p className="text-xs text-emerald-600">Available ✓</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editedContact.email || ''}
                onChange={(e) => updateEditedField('email', e.target.value)}
                data-testid="edit-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editedContact.phone || ''}
                onChange={(e) => updateEditedField('phone', e.target.value)}
                data-testid="edit-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={editedContact.industry || ''}
                onChange={(e) => updateEditedField('industry', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={editedContact.website || ''}
                onChange={(e) => updateEditedField('website', e.target.value)}
              />
            </div>
            
            <div className="space-y-2 col-span-2">
              <Label htmlFor="business_address">Business Address</Label>
              <Input
                id="business_address"
                value={editedContact.business_address || ''}
                onChange={(e) => updateEditedField('business_address', e.target.value)}
              />
            </div>
            
            <div className="col-span-2 border-t pt-4">
              <Label className="mb-3 block">Contact Types</Label>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { key: 'is_funder', label: 'Funder' },
                  { key: 'is_broker', label: 'Broker' },
                  { key: 'is_merchant', label: 'Merchant' },
                  { key: 'is_iso', label: 'ISO' },
                  { key: 'is_investor', label: 'Investor' },
                  { key: 'is_collector', label: 'Collector' },
                  { key: 'is_vendor', label: 'Vendor' },
                  { key: 'is_general', label: 'General' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={editedContact[key] || false}
                      onCheckedChange={(checked) => updateEditedField(key, checked)}
                    />
                    <Label htmlFor={key} className="text-sm font-normal">{label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => setShowEditDialog(false)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ContactMerge;
